/**
 * Email-security link scanners (Outlook SafeLinks class, CefSharp-embedded
 * browsers) detonate links from our lifecycle and review emails, and their
 * injected host-bridge code throws inside our pages:
 *
 *   "Non-Error promise rejection captured with value: Object Not Found
 *    Matching Id:N, MethodName:update, ParamCount:N"
 *
 * This is third-party noise, never our code (sentry-javascript#3440; observed
 * live 2026-07-21: welcome email sent 14:16:36Z, Sentry event on
 * /?utm_campaign=welcome at 14:17:59Z, then 18 identical PostHog $exceptions
 * in a 2-second burst, all anonymous). Filtered at every browser capture
 * point; this module is the single source of truth for the signature. The
 * review page's inline init (packages/server/src/services/review-page/
 * scripts.ts) mirrors the same check in ES5.
 */
export const SCANNER_NOISE_PATTERN = /Object Not Found Matching Id/;

interface CaptureEventLike {
  event: string;
  properties?: {
    $exception_list?: Array<{ value?: unknown } | null>;
    $exception_values?: unknown[];
  };
}

/**
 * Corporate email-security scanners that identify themselves in the user agent.
 *
 * Keep in sync with EMAIL_SECURITY_SCANNER_RE in
 * packages/server/src/routes/siteAudits.ts — the two surfaces must classify the
 * same traffic identically, and __tests__/scannerNoiseSession.test.ts reads that
 * file to fail if the server list grows and this one does not.
 *
 * Accepted tradeoff, inherited from that server list rather than decided here:
 * a corporate proxy (ZScaler, Symantec, Sophos) can append its own token to a
 * REAL employee's UA, and we would drop their analytics too. The failure mode is
 * under-counting, never a wrong count, and diverging from the server list would
 * be worse — the two would silently disagree about who is a scanner.
 */
export const EMAIL_SCANNER_UA_PATTERN =
  /GoogleImageProxy|YahooMailProxy|Barracuda|Proofpoint|Mimecast|FireEye|MessageLabs|Symantec|Sophos|ZScaler|URLDefense|SafeLinks|Outlook-iOS-Proxy/i;

/** True for a scanner that names itself in the UA. */
export function isEmailScannerUserAgent(ua?: string | null): boolean {
  return Boolean(ua) && EMAIL_SCANNER_UA_PATTERN.test(ua as string);
}

/**
 * Session latch for the scanners that do NOT name themselves.
 *
 * Measured 2026-08-15: the sessions that polluted the sign-in funnel presented a
 * plain Chrome UA (141 and 142) from Azure/Boydton, so the UA channel above
 * cannot see them. What they DO emit is the CefSharp host-bridge rejection, 2,601
 * times. Once that fires, the browser is a scanner and nothing else it reports is
 * a real user action — including the `auth_load_failed` its own Clerk timeout
 * produces and the ChunkLoadError `$exception`s that carry no scanner signature
 * and therefore survive the signature filter.
 */
let scannerSession = false;
let sessionIdentified = false;
let latchListener: ((info: { identified: boolean }) => void) | null = null;

/** Test-only: clears the latch between cases. */
export function resetScannerSession(): void {
  scannerSession = false;
  sessionIdentified = false;
}

/**
 * Mark this page load as belonging to a signed-in human, which makes it immune
 * to the latch below.
 *
 * An email-link scanner detonates a URL; it does not complete a Clerk sign-in.
 * So a session that has identified is by construction not the population the
 * latch exists to suppress, and silencing it is pure loss: measured 2026-09-10,
 * 4 of 9 external signups in the week of 09-07 emitted ZERO web events while
 * Postgres showed a finished wizard, and this is the one candidate cause we own.
 *
 * Called from analytics.ts identify(), for real users only — internal accounts
 * take the opt_out_capturing() branch above it and never reach here.
 */
export function markSessionIdentified(): void {
  sessionIdentified = true;
}

/**
 * Register the one-shot notifier fired when the latch arms, so a silenced page
 * load is COUNTABLE rather than simply absent.
 *
 * Deliberately a listener rather than a capture() call inside the filter: this
 * module is pure and synchronous, and emitting from inside posthog-js's
 * before_send is re-entrant. analytics.ts owns the key and the transport, and
 * already sends out-of-band beacons by raw fetch (captureInstallLanded).
 */
export function setScannerLatchListener(
  listener: ((info: { identified: boolean }) => void) | null,
): void {
  latchListener = listener;
}

/**
 * before_send filter: once the bridge signature has been seen, drop everything
 * else from this page load. Composed AFTER dropScannerNoiseExceptions, which is
 * what arms the latch.
 */
export function dropWhenScannerSession<T extends CaptureEventLike>(event: T | null): T | null {
  if (!event) return null;
  // An identified session outranks the latch, in BOTH orderings: the signature
  // can arrive before the identify (a slow dashboard load throws it while Clerk
  // is still resolving), so this is re-read per event rather than decided once
  // when the latch arms.
  if (sessionIdentified) return event;
  return scannerSession ? null : event;
}

/**
 * before_send filter: drop $exception events carrying the scanner signature, and
 * arm the session latch so dropWhenScannerSession can drop the rest of the
 * session's traffic too.
 */
export function dropScannerNoiseExceptions<T extends CaptureEventLike>(event: T | null): T | null {
  if (!event || event.event !== '$exception') return event;
  const list = event.properties?.$exception_list ?? [];
  const values = event.properties?.$exception_values ?? [];
  const texts = [...list.map((e) => String(e?.value ?? '')), ...values.map((v) => String(v ?? ''))];
  if (!texts.some((t) => SCANNER_NOISE_PATTERN.test(t))) return event;
  const firstArming = !scannerSession;
  scannerSession = true;
  // One notification per page load, however many times the bridge repeats: the
  // 2026-08-15 session threw this signature 2,601 times. Wrapped because a
  // listener fault must never break a capture filter, which runs on every event.
  if (firstArming && latchListener) {
    try {
      latchListener({ identified: sessionIdentified });
    } catch {
      // Analytics must never break the page.
    }
  }
  return null;
}

/**
 * The email-security sandboxes that survive BOTH filters above.
 *
 * Measured 2026-09-05..10-05 on app.overlayqa.com: 32 of the 41 people who fired
 * `auth_load_failed` were corporate mail gateways detonating our lifecycle-email
 * links (36 of their 57 rows landed 1-100 s after an `email_delivered` event).
 * They present a plain Chrome UA, so the UA pattern misses them, and they throw
 * Clerk chunk-load timeouts rather than the CefSharp signature, so the latch
 * never arms. What they cannot hide is the shape of the browser they run in: a
 * chromeless window whose viewport EXACTLY equals a 1920x1080 screen (a real
 * window loses ~100px to the tab strip and address bar), a browser timezone of
 * America/Los_Angeles while the IP sits in Toronto, Dublin, Amsterdam or Pune,
 * and no referrer, because the gateway opened the URL itself.
 *
 * TAG, never drop. `automated_viewport` mirrors the server's definition in
 * packages/server/src/lib/reportStyles.ts (viewport == screen, any size) so the
 * report pages and the dashboard agree on one name; `likely_email_sandbox` adds
 * the screen size, timezone and referrer legs. Neither is a verdict on its own:
 * a person pressing F11 on a 1080p monitor in Los Angeles who typed the URL
 * produces the same shape, which is why the product only registers the
 * properties and leaves the exclusion to the read. The geoip-vs-timezone
 * mismatch that settles it is PostHog-side (`$geoip_*`) and is not readable here.
 */
export const EMAIL_SANDBOX_SCREEN = { width: 1920, height: 1080 } as const;
export const EMAIL_SANDBOX_TIMEZONE = 'America/Los_Angeles';

export interface ViewportFingerprintInput {
  innerWidth: number;
  innerHeight: number;
  screenWidth: number;
  screenHeight: number;
  /** IANA zone from Intl, or null when the browser cannot say. */
  timeZone: string | null;
  referrer: string;
}

export interface ViewportFingerprint {
  /** Viewport equals screen: fullscreen, kiosk, or a sandbox with no chrome. */
  automated_viewport: boolean;
  /** The full measured sandbox shape; the property reads exclude on. */
  likely_email_sandbox: boolean;
}

const isPositiveFinite = (n: number): boolean => Number.isFinite(n) && n > 0;

export function readViewportFingerprint(input: ViewportFingerprintInput): ViewportFingerprint {
  const measured = [input.innerWidth, input.innerHeight, input.screenWidth, input.screenHeight].every(isPositiveFinite);
  const automatedViewport =
    measured && input.innerWidth === input.screenWidth && input.innerHeight === input.screenHeight;
  const likelyEmailSandbox =
    automatedViewport
    && input.screenWidth === EMAIL_SANDBOX_SCREEN.width
    && input.screenHeight === EMAIL_SANDBOX_SCREEN.height
    && input.timeZone === EMAIL_SANDBOX_TIMEZONE
    && input.referrer === '';
  return { automated_viewport: automatedViewport, likely_email_sandbox: likelyEmailSandbox };
}

/** Guarded reads: a missing `screen` or a throwing Intl must never break init. */
export function viewportFingerprintInput(win: Window): ViewportFingerprintInput {
  let timeZone: string | null = null;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    timeZone = null;
  }
  return {
    innerWidth: Number(win.innerWidth),
    innerHeight: Number(win.innerHeight),
    screenWidth: Number(win.screen?.width),
    screenHeight: Number(win.screen?.height),
    timeZone,
    referrer: typeof win.document?.referrer === 'string' ? win.document.referrer : '',
  };
}
